import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    LinearScale,
    Tooltip,
} from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import { Col, Row, Spin } from 'antd';
import { toast } from 'react-toastify';
import { thongKeNhanSuTheoPhongBan } from '../../services/nhanvien';
import { ThongKeNhanSuResponse } from '@app/models/nhanvien/NhanVien';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const COLORS = [
    '#185FA5', '#1D9E75', '#BA7517', '#533AB7', '#D85A30',
    '#3B6D11', '#D4537E', '#639922', '#5F5E5A', '#0F6E56',
];

const ThongKeNhanSuPhongBan = () => {
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<ChartJS | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<ThongKeNhanSuResponse[]>([]);
    const [tongSo, setTongSo] = useState(0);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const result = await thongKeNhanSuTheoPhongBan();
            setData(result);
            setTongSo(result.reduce((s, d) => s + d.soLuongNhanVien, 0));
        } catch (err: any) {
            toast.error('Không thể tải dữ liệu thống kê phòng ban!');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!chartRef.current || data.length === 0) return;
        chartInstance.current?.destroy();

        chartInstance.current = new ChartJS(chartRef.current, {
            type: 'bar',
            data: {
                labels: data.map(item => item.tenDonVi),
                datasets: [
                    {
                        label: 'Nam',
                        data: data.map(item => item.soLuongNam),
                        backgroundColor: 'rgba(24, 95, 165, 0.82)',
                        borderColor: '#185FA5',
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                        stack: 'nv',
                        barPercentage: 0.55,
                        categoryPercentage: 0.72,
                    },
                    {
                        label: 'Nữ',
                        data: data.map(item => item.soLuongNu),
                        backgroundColor: 'rgba(212, 83, 126, 0.82)',
                        borderColor: '#D4537E',
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                        stack: 'nv',
                        barPercentage: 0.55,
                        categoryPercentage: 0.72,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (items) => items[0]?.label || '',
                            label: (item) => {
                                const idx = item.dataIndex;
                                const pb = data[idx];
                                return item.dataset.label === 'Nam'
                                    ? ` Nam: ${pb.soLuongNam}`
                                    : ` Nữ: ${pb.soLuongNu}`;
                            },
                            afterBody: (items) => {
                                const idx = items[0]?.dataIndex ?? 0;
                                const pb = data[idx];
                                const pct = tongSo > 0
                                    ? ((pb.soLuongNhanVien / tongSo) * 100).toFixed(1)
                                    : '0.0';
                                return [`Tổng: ${pb.soLuongNhanVien} (${pct}%)`];
                            },
                        },
                        backgroundColor: 'rgba(7,28,80,0.92)',
                        titleColor: '#ffffff',
                        bodyColor: '#c8d8f5',
                        padding: 10,
                        cornerRadius: 6,
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: { display: false },
                        ticks: {
                            font: { size: 11 },
                            color: '#5F5E5A',
                            maxRotation: 30,
                            autoSkip: false,
                            callback: function(val, index) {
                                const label = data[index]?.tenDonVi ?? '';
                                return label.length > 18 ? label.slice(0, 17) + '…' : label;
                            },
                        },
                        border: { display: false },
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: {
                            font: { size: 11 },
                            color: '#5F5E5A',
                            precision: 0
                        },
                        border: { display: false },
                    },
                },
            },
        });

        return () => { chartInstance.current?.destroy(); };
    }, [data, tongSo]);

    const tongNam = data.reduce((s, d) => s + d.soLuongNam, 0);
    const tongNu  = data.reduce((s, d) => s + d.soLuongNu, 0);
    const maxPB   = data.length > 0
        ? data.reduce((p, c) => c.soLuongNhanVien > p.soLuongNhanVien ? c : p)
        : null;

    const cardStyle: React.CSSProperties = {
        background: '#f5f7fb',
        borderRadius: 8,
        padding: '10px 18px',
        textAlign: 'center',
        minWidth: 100,
    };

    return (
        <Col xs={24} xl={24} style={{ marginTop: 16 }}>
            <Row style={{ marginBottom: 10 }}>
                <Col span={24}>
                    <b style={{ fontSize: 20, color: '#071C50' }}>
                        Thống kê nhân sự theo phòng ban
                    </b>
                </Col>
            </Row>

            <div className="card" style={{ padding: '16px 20px' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin tip="Đang tải dữ liệu..." />
                    </div>
                ) : data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
                        Không có dữ liệu
                    </div>
                ) : (
                    <>
                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Tổng nhân viên</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#071C50' }}>{tongSo}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Số phòng ban</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#185FA5' }}>{data.length}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Tổng nam</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#185FA5' }}>{tongNam}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Tổng nữ</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#D4537E' }}>{tongNu}</div>
                                </div>
                            </Col>
                        </Row>

                        <div style={{ position: 'relative', height: 280, marginBottom: 8 }}>
                            <canvas ref={chartRef} role="img"
                                aria-label={`Biểu đồ cột thống kê ${data.length} phòng ban, tổng ${tongSo} nhân viên`}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
                            {[{ color: '#185FA5', label: 'Nam' }, { color: '#D4537E', label: 'Nữ' }].map(item => (
                                <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#5F5E5A' }}>
                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: 'inline-block' }} />
                                    {item.label}
                                </span>
                            ))}
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#eef2f9' }}>
                                        <th style={thStyle}>STT</th>
                                        <th style={{ ...thStyle, textAlign: 'left' }}>Phòng ban</th>
                                        <th style={thStyle}>Nam</th>
                                        <th style={thStyle}>Nữ</th>
                                        <th style={thStyle}>Tổng</th>
                                        <th style={thStyle}>Tỷ lệ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item, i) => {
                                        const pct = tongSo > 0 ? (item.soLuongNhanVien / tongSo) * 100 : 0;
                                        return (
                                            <tr key={item.idDonVi} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                <td style={tdStyle}>{i + 1}</td>
                                                <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                    <span style={{
                                                        display: 'inline-block', width: 8, height: 8,
                                                        borderRadius: 2, background: COLORS[i % COLORS.length],
                                                        marginRight: 7, flexShrink: 0,
                                                    }} />
                                                    <b style={{ color: '#071C50' }}>{item.tenDonVi}</b>
                                                </td>
                                                <td style={{ ...tdStyle, color: '#185FA5' }}>{item.soLuongNam}</td>
                                                <td style={{ ...tdStyle, color: '#993556' }}>{item.soLuongNu}</td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#071C50' }}>{item.soLuongNhanVien}</td>
                                                <td style={tdStyle}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                                                        <div style={{ width: 64, height: 6, background: '#e0e6f0', borderRadius: 3, overflow: 'hidden' }}>
                                                            <div style={{
                                                                width: `${pct}%`, height: '100%',
                                                                background: COLORS[i % COLORS.length],
                                                                borderRadius: 3,
                                                            }} />
                                                        </div>
                                                        <span style={{ fontSize: 11, color: '#888', minWidth: 38 }}>
                                                            {pct.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr style={{ background: '#f0f4fa', fontWeight: 600 }}>
                                        <td style={tdStyle} colSpan={2}>Tổng cộng</td>
                                        <td style={{ ...tdStyle, color: '#185FA5' }}>{tongNam}</td>
                                        <td style={{ ...tdStyle, color: '#993556' }}>{tongNu}</td>
                                        <td style={{ ...tdStyle, color: '#071C50' }}>{tongSo}</td>
                                        <td style={{ ...tdStyle, color: '#071C50' }}>100%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </Col>
    );
};

const thStyle: React.CSSProperties = {
    padding: '8px 12px', textAlign: 'center',
    fontWeight: 600, color: '#071C50', fontSize: 12, whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
    padding: '7px 12px', textAlign: 'center', color: '#3d3d3a',
};

export default ThongKeNhanSuPhongBan;
